// src/components/ProductOrderPanel.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaBarcode } from "react-icons/fa";
import styles from "../../styles/Buyer/ProductOrderPanel.module.css";
import AddressPopup from "./AddressPopup";
import AddressDropdown from "./AddressDropdown";
import { useDispatch } from "react-redux";

import { setOrderSummary } from "../../redux/slices/orderSummarySlice";

function ProductOrderPanel({ singleProduct }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [buyerAddresses, setBuyerAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [requiredQuantity, setRequiredQuantity] = useState("");
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [saveAddressLoading, setSaveAddressLoading] = useState(false);
  const [buyerAddressesLoading, setBuyerAddressesLoading] = useState(true);
  const [buyerProfile, setBuyerProfile] = useState(null);

  const [addressForm, setAddressForm] = useState({
    fullName: "",
    mobileNumber: "",
    flatHouse: "",
    areaStreet: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
  });

  const fetchBuyerAddresses = async () => {
    try {
      setBuyerAddressesLoading(true);

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setBuyerProfile(data.user);

        let allAddresses = (data.user?.addresses || []).map((address) => ({
          ...address,
          fullAddress:
            address.fullAddress ||
            `${address.flatHouse || ""}${address.areaStreet ? `, ${address.areaStreet}` : ""
            }${address.landmark ? `, ${address.landmark}` : ""}${address.city ? `, ${address.city}` : ""
            }${address.state ? `, ${address.state}` : ""}${address.pincode ? ` - ${address.pincode}` : ""
            }`,
        }));

        const shippingAddress = data.user?.businessProfile?.shippingAddress;

        if (
          shippingAddress &&
          !allAddresses.some(
            (address) => address.fullAddress === shippingAddress
          )
        ) {
          allAddresses = [
            {
              fullName:
                data.user?.fullName ||
                data.user?.businessProfile?.companyName ||
                "",

              mobileNumber:
                data.user?.businessProfile?.phoneNumber ||
                data.user?.phoneNumber ||
                "",

              flatHouse: "",
              areaStreet: "",
              landmark: "",
              city: "",
              state: "",
              pincode: "",

              fullAddress: shippingAddress,
            },
            ...allAddresses,
          ];
        }

        setBuyerAddresses(allAddresses);

        if (allAddresses.length > 0) {
          setSelectedAddress(allAddresses[0].fullAddress);
        }
        return allAddresses;
      }
    } catch (error) {
      console.log("Fetch Buyer Addresses Error:", error);
    } finally {
      setBuyerAddressesLoading(false);
    }
  };

  useEffect(() => {
    fetchBuyerAddresses();
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    // 1. Update the input field state immediately and reset city/state if pincode changes
    setAddressForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "pincode" ? { city: "", state: "" } : {}),
    }));

    // 2. If the field is pincode and exactly 6 digits, trigger API call
    if (name === "pincode" && value.length === 6) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();

        if (data && data[0]?.Status === "Success") {
          const postalInfo = data[0].PostOffice[0];

          // postalInfo.Name gives the exact area/sub-office (e.g., Dharmavaram)
          // postalInfo.District gives the broader district (e.g., Anantapur)
          const exactArea = postalInfo.Name;
          const district = postalInfo.District;

          setAddressForm((prev) => ({
            ...prev,
            // You can combine them or use exactArea as the primary city field
            city: `${exactArea}, ${district}`,
            state: postalInfo.State || "",
          }));
        } else {
          console.log("Invalid Pincode");
        }
      } catch (error) {
        console.log("Error fetching location by pincode:", error);
      }
    }
  };

  const handleSaveAddress = async () => {
    try {
      setSaveAddressLoading(true);

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/add-address`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(addressForm),
        }
      );

      const data = await response.json();

      if (response.ok) {
        const updatedAddresses = await fetchBuyerAddresses();

        const latestAddress = updatedAddresses?.[updatedAddresses.length - 1];

        if (latestAddress?.fullAddress) {
          setSelectedAddress(latestAddress.fullAddress);
        }

        setAddressForm({
          fullName: "",
          mobileNumber: "",
          flatHouse: "",
          areaStreet: "",
          landmark: "",
          city: "",
          state: "",
          pincode: "",
        });

        setShowAddressPopup(false);
      } else {
        alert(data.message || "Failed to save address");
      }
    } catch (error) {
      console.log("Save Address Error:", error);
      alert("Failed to save address");
    } finally {
      setSaveAddressLoading(false);
    }
  };

  const handleContinueToOrder = () => {
    const quantity = Number(requiredQuantity);

    if (!selectedAddress) {
      alert("Please select delivery address");
      return;
    }

    if (!quantity || quantity <= 0) {
      alert("Please enter valid quantity");
      return;
    }

    if (quantity > Number(singleProduct.quantity)) {
      alert("Required quantity cannot exceed available stock");
      return;
    }

    if (
      singleProduct.stockStatus !== "available" ||
      Number(singleProduct.quantity) <= 0
    ) {
      alert("This product is currently out of stock");
      return;
    }

    const selectedAddressObject =
      buyerAddresses.find(
        (address) => address.fullAddress?.trim() === selectedAddress?.trim()
      ) || buyerAddresses[0];

    if (!selectedAddressObject) {
      alert("Selected address not found");
      return;
    }

    const orderData = {
      sellerId: singleProduct.seller?._id || "",
      sellerName: singleProduct.seller?.businessProfile?.companyName || "",
      shippingAddress: selectedAddressObject,
      buyerGstNumber: buyerProfile?.businessProfile?.gstNumber || "",
      businessProfile: buyerProfile?.businessProfile || {},
      buyerEmail:
        buyerProfile?.email || buyerProfile?.businessProfile?.email || "",

      orderItems: [
        {
          product: singleProduct._id || "",
          seller: singleProduct.seller?._id || "",
          category: singleProduct.category || "",
          application: singleProduct.application || "",
          requiredQuantity: quantity,
          pricePerMT: Number(singleProduct.pricePerMT || 0),
          subtotal: quantity * Number(singleProduct.pricePerMT || 0),
          loadingLocation: singleProduct.loadingLocation || "",
          hsnCode: singleProduct.hsnCode || "",
          productImage: singleProduct.images?.[0]?.image || "",
          availableQuantity: Number(singleProduct.quantity || 0),
        },
      ],
    };

    dispatch(setOrderSummary(orderData));
    navigate("/order-summary");
  };

  return (
    <>
      <div className={styles.sidePanelWrapper}>
        <div className={styles.businessCard}>
          <h3>Seller Information</h3>

          <div className={styles.businessBox}>
            <FaBarcode className={styles.cardIcon} />
            <div>
              <span>Company ID</span>
              <h4>
                {singleProduct.seller?.businessProfile?.companyId ||
                  "Not Available"}
              </h4>
            </div>
          </div>

          <div className={styles.verifiedBadge}>
            <div className={styles.badgeOrbit}></div>

            <div className={styles.verifiedSeal}>
              {singleProduct.seller?.isVerified ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.2 12.8L11 14.6L15.5 10.1"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 7V12"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="16.5" r="1" fill="currentColor" />
                </svg>
              )}
            </div>

            <div className={styles.verifiedText}>
              <span className={styles.title}>
                {singleProduct.seller?.isVerified
                  ? "Verified Seller"
                  : "Verification Pending"}
              </span>

              <span className={styles.status}>
                {singleProduct.seller?.isVerified
                  ? "Trusted & Verified"
                  : "Seller verification is in Progress"}
              </span>
            </div>

            <div className={styles.liveDot}></div>
          </div>

          <div className={styles.stockInfo}>
            {singleProduct.stockStatus === "available"
              ? "✔ Product Available In Stock"
              : "Currently Out Of Stock"}
          </div>
        </div>

        <div className={styles.orderCard}>
          <h3>Order Details</h3>

          <AddressDropdown
            buyerAddresses={buyerAddresses}
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            buyerAddressesLoading={buyerAddressesLoading}
          />

          <button
            type="button"
            className={styles.addAddressBtn}
            onClick={() => setShowAddressPopup(true)}
          >
            + Add New Address
          </button>

          <div className={styles.quantitySection}>
            <label>Required Quantity (MT)</label>

            <input
              type="number"
              min="1"
              max={singleProduct.quantity}
              placeholder="Enter Required Quantity"
              className={styles.quantityInput}
              value={requiredQuantity}
              onChange={(e) => setRequiredQuantity(e.target.value)}
            />

            {requiredQuantity > singleProduct.quantity && (
              <p className={styles.quantityError}>
                Required quantity cannot exceed available stock.
              </p>
            )}
          </div>

          <button
            className={styles.buyButton}
            onClick={handleContinueToOrder}
            disabled={
              singleProduct.stockStatus !== "available" ||
              Number(singleProduct.quantity) <= 0 ||
              !selectedAddress ||
              !requiredQuantity ||
              Number(requiredQuantity) <= 0 ||
              Number(requiredQuantity) > Number(singleProduct.quantity)
            }
          >
            {singleProduct.stockStatus !== "available" ||
              Number(singleProduct.quantity) <= 0
              ? "Currently Unavailable"
              : "Continue To Order"}
          </button>
        </div>
      </div>

      <AddressPopup
        showAddressPopup={showAddressPopup}
        setShowAddressPopup={setShowAddressPopup}
        addressForm={addressForm}
        handleInputChange={handleInputChange}
        handleSaveAddress={handleSaveAddress}
        saveAddressLoading={saveAddressLoading}
      />
    </>
  );
}

export default ProductOrderPanel;